import React from "react";
import {
  NumberFormatBase,
  NumericFormat,
  PatternFormat,
} from "react-number-format";

// Estes componentes são usados como `inputComponent` do TextInput, que passa
// value, onChange e os atributos do <input>. Todo wrapper é forwardRef e
// repassa o ref no `getInputRef`, para o `inputRef` do TextInput chegar no
// <input> de verdade.
//
// Na v5 do react-number-format o export default sumiu e o componente único foi
// quebrado em três. O `NumberInput` continua sendo a fachada genérica: escolhe
// qual dos três usar a partir do tipo do `format` recebido.
const NumberInput = React.forwardRef(function NumberInput(props, ref) {
  // `allowNegative` é prop do NumericFormat apenas. Se sobrar no spread, o
  // PatternFormat e o NumberFormatBase repassam para o <input> e o React
  // reclama de atributo desconhecido no DOM.
  const { onChange, format, allowNegative, ...other } = props;

  const shared = {
    // Todo campo daqui só aceita dígitos (celular, CPF, CEP, dinheiro, CVV):
    // no celular, abre o teclado numérico em vez do alfabético.
    inputMode: "numeric",
    ...other,
    getInputRef: ref,
    onValueChange: (values) => {
      onChange({
        target: {
          value: values.value,
        },
      });
    },
    valueIsNumericString: true, // era `isNumericString` na v4
  };

  // Máscara de posições fixas: "(##) #####-####", "#####-###" etc.
  if (typeof format === "string") {
    return <PatternFormat {...shared} format={format} />;
  }

  // `format` como função (MoneyInput). O NumberFormatBase é o único da v5 que
  // ainda aceita um formatador customizado, e o `removeFormatting` default dele
  // — remover tudo que não é dígito — é exatamente o que a v4 fazia. Assim o
  // campo continua sendo "digite em centavos, exiba em reais", e o valor que
  // sai no onValueChange continua sendo a string crua de dígitos.
  if (typeof format === "function") {
    return <NumberFormatBase {...shared} format={format} />;
  }

  // Sem `format`: número puro. É o único dos três que entende `allowNegative`.
  return <NumericFormat {...shared} allowNegative={allowNegative} />;
});

const AddressNumberInput = React.forwardRef(
  function AddressNumberInput(props, ref) {
    return <NumberInput {...props} ref={ref} thousandSeparator={false} />;
  },
);

const PhoneInput = React.forwardRef(function PhoneInput(props, ref) {
  const format =
    props.value.length === 11 ? "(##) #####-####" : "(##) ####-#####";
  return <NumberInput {...props} ref={ref} format={format} />;
});

const CPFCNPJInput = React.forwardRef(function CPFCNPJInput(props, ref) {
  const format =
    props.value.length <= 11 ? "###.###.###-###" : "##.###.###/####-##";
  return <NumberInput {...props} ref={ref} format={format} />;
});

const CEPInput = React.forwardRef(function CEPInput(props, ref) {
  return <NumberInput {...props} ref={ref} format="#####-###" />;
});

const MoneyInput = React.forwardRef(function MoneyInput(props, ref) {
  // Sem `allowNegative`: o campo só aceita dígitos (o removeFormatting default
  // do NumberFormatBase descarta o resto), então negativo é impossível de
  // qualquer forma.
  return <NumberInput {...props} ref={ref} format={currencyFormatter} />;
});

function currencyFormatter(value) {
  if (!Number(value)) return "";

  const amount = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value / 100);

  return `${amount}`;
}

const CreditCardNumberInput = React.forwardRef(
  function CreditCardNumberInput(props, ref) {
    return <NumberInput {...props} ref={ref} format="#### #### #### ####" />;
  },
);

const CreditCardValidInput = React.forwardRef(
  function CreditCardValidInput(props, ref) {
    return <NumberInput {...props} ref={ref} format="## / ##" />;
  },
);

const CreditCardCVVInput = React.forwardRef(
  function CreditCardCVVInput(props, ref) {
    return <NumberInput format="####" {...props} ref={ref} />;
  },
);

// Diferente dos demais, este NÃO passa pelo NumberInput: recebe os props do
// TextInput direto, inclusive o onChange. Mantido assim de
// propósito — encaminhar onChange como os outros mudaria o valor gravado de
// "12-34-5678" para "12345678", que é contrato com o backend.
const BirthdayInput = React.forwardRef(function BirthdayInput(props, ref) {
  return <PatternFormat format="##-##-####" {...props} getInputRef={ref} />;
});

export default NumberInput;

export {
  AddressNumberInput,
  PhoneInput,
  CPFCNPJInput,
  CEPInput,
  MoneyInput,
  BirthdayInput,
  CreditCardNumberInput,
  CreditCardValidInput,
  CreditCardCVVInput,
};
